import React, { useState, useEffect } from 'react';
import { Box, Container, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, TextField, CircularProgress, Typography, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Grid, FormControl, InputLabel, Select, MenuItem, Chip } from '@mui/material';
import { subAdminService } from '../services/api';
import { Add, Edit, Delete, Search, TrendingUp, Shield } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';

const SubAdminPage = () => {
  const navigate = useNavigate();
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, itemId: null, isDeleting: false });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at-desc');
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Fetch sub-admins whenever search, sort, or page changes
  useEffect(() => {
    fetchSubAdmins();
  }, [searchQuery, sortBy, pageNumber]);

  const fetchSubAdmins = async () => {
    try {
      setLoading(true);
      const response = await subAdminService.getAllSubAdmins({
        page: pageNumber,
        limit: 10,
        search: searchQuery,
        sort: sortBy
      });
      
      let data = [];
      let paginationData = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
      };

      if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
        paginationData = response.data.pagination || paginationData;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.success && response.data?.data) {
        data = response.data.data;
        paginationData = response.data.pagination || paginationData;
      }
      
      setSubAdmins(data);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error fetching sub-admins:', error);
      setSubAdmins([]);
      setToast({ open: true, message: 'Error loading sub-admins', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubAdmin = () => {
    navigate('/admin/sub-admins/add');
  };

  const handleEditSubAdmin = (subAdmin) => {
    navigate(`/admin/sub-admins/edit/${subAdmin.id}`, { state: { subAdmin } });
  };

  const handleDeleteClick = (subAdmin) => {
    setConfirmDialog({
      open: true,
      itemId: subAdmin.id,
      itemName: subAdmin.username,
      isDeleting: true
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await subAdminService.deleteSubAdmin(confirmDialog.itemId);
      setToast({ open: true, message: 'Sub-admin deleted successfully', type: 'success' });
      setConfirmDialog({ open: false, itemId: null, isDeleting: false });
      await fetchSubAdmins();
    } catch (error) {
      console.error('Error deleting sub-admin:', error);
      setToast({ open: true, message: 'Error deleting sub-admin: ' + (error.response?.data?.error || error.message), type: 'error' });
    }
  };

  const filteredSubAdmins = subAdmins;
  const totalPages = Math.ceil(pagination.totalItems / pagination.itemsPerPage);
  const startIdx = (pageNumber - 1) * pagination.itemsPerPage;
  const endIdx = startIdx + pagination.itemsPerPage;
  const paginatedSubAdmins = filteredSubAdmins.slice(startIdx, endIdx);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
                Sub Admin Management
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Manage sub-administrators and their permissions
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={handleAddSubAdmin}
              sx={{ backgroundColor: '#2c5f2d', '&:hover': { backgroundColor: '#1f4620' } }}
            >
              Add Sub Admin
            </Button>
          </Box>
        </Box>

        {/* Stats Summary */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
          {/* Total Sub Admins Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#dcfce7', p: 2, borderRadius: '12px' }}>
                <TrendingUp sx={{ color: '#2c5f2d', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Total Sub Admins
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c5f2d', mt: 0.5 }}>
                  {pagination.totalItems}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Active administrators
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Active Sub Admins Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#e0e7ff', p: 2, borderRadius: '12px' }}>
                <Shield sx={{ color: '#4f46e5', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  With Permissions
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4f46e5', mt: 0.5 }}>
                  {subAdmins.filter(sa => sa.permissions && Object.values(sa.permissions).some(p => p)).length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Configured
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Restricted Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#fef3c7', p: 2, borderRadius: '12px' }}>
                <TrendingUp sx={{ color: '#d97706', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Limited Access
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706', mt: 0.5 }}>
                  {subAdmins.filter(sa => sa.permissions && !Object.values(sa.permissions).every(p => p)).length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Restricted modules
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Search and Filter Section */}
        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search by username or email..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPageNumber(1);
                }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: '#667085' }} />,
                }}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e8ecf1' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPageNumber(1);
                  }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="created_at-desc">Newest First</MenuItem>
                  <MenuItem value="created_at-asc">Oldest First</MenuItem>
                  <MenuItem value="name-asc">Name A-Z</MenuItem>
                  <MenuItem value="name-desc">Name Z-A</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        {/* Sub Admins Table */}
        <Card sx={{
          border: '1px solid #e8ecf1',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb', borderBottom: '2px solid #e8ecf1' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>Username</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Permissions</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedSubAdmins && paginatedSubAdmins.length > 0 ? (
                  paginatedSubAdmins.map(subAdmin => {
                    const permCount = subAdmin.permissions ? Object.values(subAdmin.permissions).filter(p => p).length : 0;
                    return (
                      <TableRow 
                        key={subAdmin.id}
                        sx={{ 
                          '&:hover': { backgroundColor: '#f8fafb' },
                          borderBottom: '1px solid #e8ecf1'
                        }}
                      >
                        <TableCell sx={{ fontWeight: '500', color: '#1a202c' }}>{subAdmin.username}</TableCell>
                        <TableCell sx={{ color: '#667085' }}>{subAdmin.email}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${permCount} permission${permCount !== 1 ? 's' : ''}`}
                            size="small"
                            sx={{ 
                              backgroundColor: permCount > 0 ? '#dcfce7' : '#fee2e2',
                              color: permCount > 0 ? '#2c5f2d' : '#dc2626',
                              fontWeight: '500'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={subAdmin.is_active ? 'Active' : 'Inactive'}
                            color={subAdmin.is_active ? 'success' : 'error'}
                            variant="filled"
                            size="small"
                            sx={{ fontWeight: '500' }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#667085' }}>{new Date(subAdmin.created_at).toLocaleDateString()}</TableCell>
                        <TableCell sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            size="small"
                            variant="outlined"
                            startIcon={<Edit />}
                            onClick={() => handleEditSubAdmin(subAdmin)}
                            sx={{ 
                              borderColor: '#2c5f2d',
                              color: '#2c5f2d',
                              '&:hover': { borderColor: '#1f4620', backgroundColor: '#f0f9f0' }
                            }}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="small"
                            variant="outlined"
                            startIcon={<Delete />}
                            onClick={() => handleDeleteClick(subAdmin)}
                            sx={{ 
                              borderColor: '#dc2626',
                              color: '#dc2626',
                              '&:hover': { borderColor: '#991b1b', backgroundColor: '#fee2e2' }
                            }}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3, color: '#667085' }}>
                      No sub-admins found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 1, borderTop: '1px solid #e8ecf1' }}>
              <Button
                variant="outlined"
                size="small"
                disabled={pageNumber === 1}
                onClick={() => setPageNumber(pageNumber - 1)}
              >
                Previous
              </Button>
              {[...Array(totalPages)].map((_, i) => (
                <Button
                  key={i + 1}
                  variant={pageNumber === i + 1 ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setPageNumber(i + 1)}
                  sx={{
                    minWidth: '36px',
                    backgroundColor: pageNumber === i + 1 ? '#2c5f2d' : 'transparent',
                    color: pageNumber === i + 1 ? '#fff' : '#667085',
                    borderColor: '#d5d9e2',
                    '&:hover': {
                      backgroundColor: pageNumber === i + 1 ? '#2c5f2d' : '#f3f4f6',
                      borderColor: '#667085'
                    }
                  }}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                variant="outlined"
                size="small"
                disabled={pageNumber === totalPages || totalPages === 0}
                onClick={() => setPageNumber(pageNumber + 1)}
              >
                Next
              </Button>
            </Box>
          )}
        </Card>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.type} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete Sub Admin"
        message={`Are you sure you want to delete "${confirmDialog.itemName}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDialog({ open: false, itemId: null, isDeleting: false })}
        isLoading={false}
      />
    </Box>
  );
};

export default SubAdminPage;
